import { useCallback, useEffect, useContext, useState, useRef } from "react";
import { useServiceCenter } from "../contexts";
import UserContext from "../../../contexts/UserContext";
import NotificationsContext from "../../../contexts/NotificationsContext";
import InstagramApiService from "../../InstagramConfig/services/InstagramApiService";
import MessengerApiService from "../../MessengerConfig/services/MessengerApiService";

/**
 * Hook para integrar ServiceCenter com UserContext e NotificationsContext
 * Este hook é responsável por:
 * 1. Carregar contas WhatsApp do bottyData (UserContext)
 * 2. Sincronizar unreadCounts com NotificationsContext
 * 3. Gerenciar seleção de conta
 */
const useServiceCenterData = () => {
  const {
    accounts,
    selectedAccount,
    setAccounts,
    setSelectedAccount,
    setUnreadCount,
    unreadCounts: localUnreadCounts,
  } = useServiceCenter();

  const { bottyData, accountLoading } = useContext(UserContext);
  const { unreadCounts: globalUnreadCounts, setNotify } = useContext(NotificationsContext);

  const [isInitialized, setIsInitialized] = useState(false);
  const [profilePictures, setProfilePictures] = useState({});
  const initRef = useRef(false);

  /**
   * Carrega foto do perfil de uma conta
   */
  const loadProfilePicture = useCallback(async (account) => {
    if (!account?.phone || profilePictures[account.phone]) return null;

    try {
      // A foto de perfil pode vir do próprio bottyData ou de uma API
      if (account.profile_picture) {
        setProfilePictures((prev) => ({
          ...prev,
          [account.phone]: account.profile_picture,
        }));
        return account.profile_picture;
      }
    } catch (error) {
      console.error("Erro ao carregar foto de perfil:", error);
    }
    return null;
  }, [profilePictures]);

  /**
   * Processa bottyData (WhatsApp WAHA + Cloud) + Instagram accounts.
   * Constraint do produto: máx 1 conta por canal por account.
   */
  useEffect(() => {
    // Aguarda o carregamento terminar
    if (accountLoading || initRef.current) return;

    if (bottyData === undefined) return;

    const init = async () => {
      // 1. Contas WhatsApp (WAHA + Cloud) — vêm do bottyData global
      const activeWa = Array.isArray(bottyData)
        ? bottyData.filter(
            (a) => a.status === "WORKING" && !a.is_public_test,
          )
        : [];

      const waAccounts = activeWa.map((account) => ({
        id: account.id,
        uid: account.uid,
        unique: account.unique,
        phone: account.phone,
        token: account.token,
        status: account.status,
        receive_chats: account.receive_chats,
        ai_key_id: account.ai_key_id,
        profile_picture: account.profile_picture || null,
        createdAt: account.createdAt,
        provider: account.provider || "waha",
        phone_number_id: account.phone_number_id || null,
        display_phone_number: account.display_phone_number || null,
        agent_flow_id: account.agent_flow_id || null,
        channel: "whatsapp",
        provider_account_id: account.phone_number_id || account.phone,
        provider_identity: account.display_phone_number || account.phone,
      }));

      // 2. Contas Instagram — vêm de endpoint dedicado
      let igAccounts = [];
      try {
        const resp = await InstagramApiService.listAccounts();
        const items = (resp?.data || []).filter((ig) => ig.is_active);
        igAccounts = items.map((ig) => ({
          id: ig.id,
          uid: ig.ig_business_account_id,
          phone: ig.ig_business_account_id, // identificador opaco (PSID/IGSID escopo)
          status: "WORKING",
          profile_picture: ig.profile_picture_url || null,
          provider: "instagram",
          agent_flow_id: ig.agent_flow_id || null,
          channel: "instagram",
          provider_account_id: ig.id,
          provider_identity: ig.username ? `@${ig.username}` : "Instagram",
        }));
      } catch (err) {
        console.warn("Falha ao listar contas Instagram:", err);
      }

      // 3. Contas Messenger — endpoint dedicado
      let msgAccounts = [];
      try {
        const resp = await MessengerApiService.listAccounts();
        const items = (resp?.data || []).filter((m) => m.is_active);
        msgAccounts = items.map((m) => ({
          id: m.id,
          uid: m.facebook_page_id,
          phone: m.facebook_page_id, // identificador opaco (Page ID)
          status: "WORKING",
          profile_picture: m.profile_picture_url || null,
          provider: "messenger",
          agent_flow_id: m.agent_flow_id || null,
          channel: "messenger",
          provider_account_id: m.id,
          provider_identity: m.page_name || "Messenger",
        }));
      } catch (err) {
        console.warn("Falha ao listar contas Messenger:", err);
      }

      const allAccounts = [...waAccounts, ...igAccounts, ...msgAccounts];
      setAccounts(allAccounts);

      // Seleciona primeira conta se nada selecionado (mantém compat com
      // código legado — o fluxo multi-canal usa selectedConversation.channel
      // diretamente, não dependendo mais do selectedAccount.channel).
      if (!selectedAccount && allAccounts.length > 0) {
        const first = allAccounts[0];
        setSelectedAccount(first, first.profile_picture);
        if (first.channel === "whatsapp") {
          loadProfilePicture(first);
        }
      }

      initRef.current = true;
      setIsInitialized(true);
    };

    init();
  }, [
    bottyData,
    accountLoading,
    selectedAccount,
    setAccounts,
    setSelectedAccount,
    loadProfilePicture,
  ]);

  /**
   * Sincroniza unreadCounts do NotificationsContext para o ServiceCenter
   */
  useEffect(() => {
    if (globalUnreadCounts && typeof globalUnreadCounts === "object") {
      Object.entries(globalUnreadCounts).forEach(([phone, count]) => {
        if (localUnreadCounts[phone] !== count) {
          setUnreadCount(phone, count);
        }
      });
    }
  }, [globalUnreadCounts, localUnreadCounts, setUnreadCount]);

  /**
   * Seleciona uma conta
   */
  const selectAccount = useCallback(
    async (account) => {
      if (!account) return;

      const profilePic = profilePictures[account.phone] || account.profile_picture;
      setSelectedAccount(account, profilePic);

      // Carregar foto se não tiver
      if (!profilePic) {
        await loadProfilePicture(account);
      }
    },
    [setSelectedAccount, profilePictures, loadProfilePicture]
  );

  /**
   * Seleciona conta por índice
   */
  const selectAccountByIndex = useCallback(
    (index) => {
      if (accounts[index]) {
        selectAccount(accounts[index]);
      }
    },
    [accounts, selectAccount]
  );

  /**
   * Retorna índice da conta selecionada
   */
  const selectedAccountIndex = accounts.findIndex(
    (acc) => acc.phone === selectedAccount?.phone
  );

  return {
    // Estado
    accounts,
    selectedAccount,
    selectedAccountIndex,
    isInitialized,
    isLoading: accountLoading,
    profilePictures,

    // Actions
    selectAccount,
    selectAccountByIndex,

    // Utils
    setNotify,
  };
};

export default useServiceCenterData;
